
import { storeToRefs } from 'pinia';
import { ValidationObserver } from 'vee-validate';
import { computed, defineComponent, inject, nextTick, ref, shallowRef } from '@vue/composition-api';
import { onUnmounted, useContext, useRoute } from '@nuxtjs/composition-api';
import { Button, Checkbox, Modal, StatusBlock } from '05-ui-kit';
import cloneDeep from 'lodash.clonedeep';
import { declination } from '@artmizu/utils';
import Tooltip from '05-ui-kit/lib/Tooltip';
import type { PartialDeep } from 'type-fest';
import addNotification from '~/helper/notifications';
import { useLocationStore } from '~/store/location';
import { useCartStore } from '~/store/cart';
import { useResizeStore } from '~/store/resize';
import { useUserStore } from '~/store/user';
import { useUserToolStore } from '~/store/userTool';
import CheckoutController from '~/components/Checkout/CheckoutController/CheckoutController.vue';
import Unavailable from '~/components/Unavailable/Unavailable.vue';
import StepController from '~/components/Checkout/StepController/StepController.vue';
import StepContact from '~/components/Checkout/StepContact/StepContact.vue';
import StepDelivery from '~/components/Checkout/StepDelivery/StepDelivery.vue';
import StepPayment from '~/components/Checkout/StepPayment/StepPayment.vue';
import CartProductList from '~/components/CartProductList/CartProductList.vue';
import Summary from '~/components/Checkout/Summary/Summary.vue';
import OrderSummaryMobile from '~/components/Checkout/OrderSummaryMobile/OrderSummaryMobile.vue';
import ErrorNote from '~/components/ErrorNote/index.vue';
import InfoBonus from '~/components/InfoBonus/InfoBonus.vue';
import UIActionEntity from '~/data/UIActionEntity/UIActionEntity.vue';
import AccessoriesProductCard from '~/components/AccessoriesProductCard/AccessoriesProductCard.vue';
import RowSliderNew from '~/components/sliders/RowSliderNew/RowSliderNew.vue';
import type { EmitteryType } from '~/plugins/emitter';
import type { CartProduct } from '~/type/product/Product';
import type { Location } from '~/type/Location';
import type { NewOrder } from '~/type/NewOrder';
import type SmartComponentCallbackParams from '~/type/SmartComponentCallbackParams';
import type { ObserverRefVeeValidate } from '~/type/ObserverRefVeeValidate';
type RequestType = Awaited<ReturnType<typeof context.$api.order.getOrderData>>;
const __sfc_main = defineComponent({
  head: {
    title: 'Корзина и оформление заказа'
  }
});
__sfc_main.setup = (__props, __ctx) => {
  const modal = ref(false);
  const newCity = ref<Location>();
  const analyticSended = ref(false);
  const context = useContext();
  const controllerNavigation = [{
    name: 'Контакты',
    value: 'contact'
  }, {
    name: 'Доставка',
    value: 'delivery'
  }, {
    name: 'Оплата',
    value: 'payment'
  }, {
    name: 'Итого',
    value: 'summary'
  }];
  const userStore = useUserStore();
  const resizeStore = useResizeStore();
  const {
    onChangeCart
  } = useUserToolStore();
  const route = useRoute();
  const isMobile = computed(() => resizeStore.resize && resizeStore.resize <= 900);
  const isGuest = computed(() => userStore.isGuest);
  const emitter = inject<EmitteryType>('emitter')!;
  const {
    currentCity: city,
    code: location
  } = storeToRefs(useLocationStore());
  const {
    setCurrentCity
  } = useLocationStore();
  const {
    setList: cartSetList,
    reset: cartReset
  } = useCartStore();
  const cartStore = useCartStore();
  const cartData = computed(() => cartStore.data);
  const cartCount = computed(() => cartStore.cartAmount);
  const mobileErrorNote = shallowRef<InstanceType<typeof ErrorNote>>();
  const mobileContactStep = shallowRef<ObserverRefVeeValidate>();
  const mobileDeliveryStep = shallowRef<ObserverRefVeeValidate>();
  const mobilePaymentStep = shallowRef<ObserverRefVeeValidate>();
  const mobileSummaryStep = shallowRef<ObserverRefVeeValidate>();
  const checkoutModal = shallowRef<InstanceType<typeof Modal>>();
  let requestOrderData: () => void;

  /**
  * Подписываемся на глобальное изменение корзины из других мест,
  * например из окна в панельке пользователя
  */

  const cartListener = cartStore.$onAction(({
    name,
    after
  }) => {
    after(() => {
      if (['remove', 'add'].includes(name)) requestOrderData?.();
    });
  });
  const userListener = userStore.$onAction(({
    name,
    after
  }) => {
    after(() => {
      if (['setData', 'logout'].includes(name)) requestOrderData?.();
    });
  });
  if (route?.value.query.step ? +route?.value.query.step >= 0 && isMobile : false) enableModal();
  function enableModal() {
    modal.value = true;
  }
  onUnmounted(() => {
    cartListener?.();
    userListener?.();
  });
  function formatQuantity(num: number) {
    return `${num} ${declination(num, ['товар', 'товара', 'товаров'])}`;
  }
  function onOrderCreate({
    event,
    order,
    value,
    activeIds
  }: {
    event: SmartComponentCallbackParams<{
      id: number;
    }>;
    order: RequestType;
    value: PartialDeep<NewOrder>;
    activeIds: number[];
  }) {
    const productList = order.product.filter((p: CartProduct) => activeIds.includes(p.id));
    const productStoreObject = cartData.value;
    activeIds.forEach((id: number) => {
      delete productStoreObject[id];
    });

    /**
      * Очищаем корзину, после успешного создания заказа, убираем из неё только активные заказы
    */
    cartListener?.();
    cartSetList(productStoreObject);

    /**
      * Делаем прокрутку к верху экрана чтобы пользователь увидел сообщение об успешном оформлении заказа
    */
    window.scrollTo(0, 0);
    const productWithMaxPrice = productList.reduce((max, product) => {
      if (product.price?.common.value && max.price?.common?.value) return +product.price.common.value > max.price.common?.value ? product : max;else return max;
    });
    if (productWithMaxPrice?.id) {
      const remind = userStore.isUser ? 1000 * 60 * 60 * 24 * 8 : 1000 * 60 * 60 * 24;
      addNotification({
        key: 'order',
        remind,
        date: Date.now(),
        payload: {
          productId: productWithMaxPrice.id,
          date: Date.now()
        }
      });
      emitter.emit('notification-order-review-created');
    }
    emitter.emit('after-checkout', {
      userId: userStore.user?.id,
      order: {
        ...order,
        ...value,
        id: event?.response.id,
        deliveryType: value.delivery?.type?.title
      }
    });
  }
  function onLoadOrderData({
    response,
    get
  }: {
    response: RequestType;
    get: () => typeof context.$api.order.getOrderData;
  }) {
    requestOrderData = get;
    if (process.client && !analyticSended.value && response?.product) {
      emitter.emit('begin-checkout', {
        productList: response?.product
      });
      analyticSended.value = true;
    }
  }
  function yandexMetrickaNextStep(currentStep: number) {
    return emitter.emit('click-next-checkout-step', {
      step: currentStep
    });
  }
  function onSuccessRemoveProducts(data: number[]) {
    const cart = cloneDeep(cartData.value);
    data.forEach(productID => {
      delete cart[productID];
    });
    cartReset();
    cartSetList(cart);
    requestOrderData?.();
    emitter.emit('cart-product-remove', {
      product: {
        quantity: data.length
      }
    });
  }
  function onErrorRemoveProducts() {
    Tooltip({
      type: 'error',
      title: 'Ошибка',
      description: 'Не получилось удалить выбранные товары, попробуйте позже',
      mobileOffset: [5, 5, 63, 5]
    });
  }
  function reload() {
    location.reload();
  }
  onUnmounted(() => {
    document.body.classList.remove('modal-overflow');
  });
  return {
    nextTick,
    modal,
    newCity,
    controllerNavigation,
    onChangeCart,
    isMobile,
    isGuest,
    emitter,
    city,
    location,
    setCurrentCity,
    cartCount,
    mobileErrorNote,
    mobileContactStep,
    mobileDeliveryStep,
    mobilePaymentStep,
    mobileSummaryStep,
    checkoutModal,
    enableModal,
    formatQuantity,
    onOrderCreate,
    onLoadOrderData,
    yandexMetrickaNextStep,
    onSuccessRemoveProducts,
    onErrorRemoveProducts,
    reload
  };
};
__sfc_main.components = Object.assign({
  CheckoutController,
  UIActionEntity,
  StatusBlock,
  Button,
  Unavailable,
  Modal,
  StepController,
  ValidationObserver,
  StepContact,
  StepDelivery,
  StepPayment,
  OrderSummaryMobile,
  Summary,
  ErrorNote,
  Checkbox,
  CartProductList,
  RowSliderNew,
  AccessoriesProductCard,
  InfoBonus
}, __sfc_main.components);
export default __sfc_main;
